import React, { useState, useRef } from 'react'
import PropTypes from 'prop-types'
import {
	Calendar,
	Clock,
	Building2,
	Briefcase,
	Globe,
	Target,
	FileText,
	Edit2,
	Shield,
	Info,
	Save,
	Upload,
	Download,
	Trash2,
	File,
	ExternalLink,
	AlertCircle,
	Paperclip,
} from 'lucide-react'
import { Button, Modal, Input, Select, Textarea } from '@components/common'
import projectDocumentService from '@services/projectDocumentService'

/**
 * DetailsTab Component
 *
 * Displays project details organized in editable blocks
 * Each block can be edited individually via modal
 */
const DetailsTab = ({ project, certificates, documents, canEdit, onUpdateProject, onRefreshDocuments }) => {
	const [editingBlock, setEditingBlock] = useState(null)
	const [editFormData, setEditFormData] = useState({})
	const [saving, setSaving] = useState(false)

	// Document upload states
	const [showUploadModal, setShowUploadModal] = useState(false)
	const [uploading, setUploading] = useState(false)
	const [uploadProgress, setUploadProgress] = useState(0)
	const [uploadError, setUploadError] = useState(null)
	const [selectedFile, setSelectedFile] = useState(null)
	const [documentType, setDocumentType] = useState('other')
	const [documentDescription, setDocumentDescription] = useState('')
	const [deletingDocId, setDeletingDocId] = useState(null)
	const fileInputRef = useRef(null)

	// Format date for display
	const formatDate = (dateString) => {
		if (!dateString) return '-'
		return new Date(dateString).toLocaleDateString('en-US', {
			month: 'long',
			day: 'numeric',
			year: 'numeric',
		})
	}

	// Format date for input
	const formatDateForInput = (dateString) => {
		if (!dateString) return ''
		const date = new Date(dateString)
		return date.toISOString().split('T')[0]
	}

	// Calculate days remaining
	const getDaysRemaining = () => {
		if (!project.target_date) return null
		const today = new Date()
		const targetDate = new Date(project.target_date)
		const diffTime = targetDate - today
		const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

		if (diffDays < 0) {
			return { value: Math.abs(diffDays), label: 'days overdue', isOverdue: true }
		} else if (diffDays === 0) {
			return { value: 0, label: 'Due today', isOverdue: false }
		} else {
			return { value: diffDays, label: 'days remaining', isOverdue: false }
		}
	}

	// Open edit modal for a block
	const handleEditBlock = (blockType) => {
		setEditingBlock(blockType)

		switch (blockType) {
			case 'basic':
				setEditFormData({
					name: project.name || '',
					description: project.description || '',
				})
				break
			case 'dates':
				setEditFormData({
					start_date: formatDateForInput(project.start_date),
					target_date: formatDateForInput(project.target_date),
				})
				break
			case 'business':
				setEditFormData({
					industry: project.industry || '',
					domain: project.domain || '',
					type: project.type || '',
				})
				break
			default:
				setEditFormData({})
		}
	}

	// Close edit modal
	const handleCloseModal = () => {
		setEditingBlock(null)
		setEditFormData({})
	}

	// Save edited data
	const handleSaveBlock = async () => {
		setSaving(true)
		try {
			await onUpdateProject?.(editFormData)
			handleCloseModal()
		} catch (error) {
			console.error('Failed to update project:', error)
		} finally {
			setSaving(false)
		}
	}

	// Handle form field change
	const handleFieldChange = (field, value) => {
		setEditFormData((prev) => ({
			...prev,
			[field]: value,
		}))
	}

	const daysInfo = getDaysRemaining()

	// Project type options
	const projectTypeOptions = [
		{ value: 'initial', label: 'Initial Certification' },
		{ value: 'renewal', label: 'Renewal' },
		{ value: 'surveillance', label: 'Surveillance Audit' },
		{ value: 'gap_analysis', label: 'Gap Analysis' },
	]

	// Document type options
	const documentTypeOptions = projectDocumentService.getDocumentTypes()

	// Handle file selection
	const handleFileSelect = (e) => {
		const file = e.target.files?.[0]
		if (file) {
			const validation = projectDocumentService.validateFile(file)
			if (!validation.valid) {
				setUploadError(validation.errors.join(', '))
				return
			}
			setSelectedFile(file)
			setUploadError(null)
		}
	}

	// Handle document upload
	const handleUploadDocument = async () => {
		if (!selectedFile) return

		setUploading(true)
		setUploadError(null)
		setUploadProgress(0)

		try {
			await projectDocumentService.upload(
				selectedFile,
				{
					projectId: project.id,
					documentType,
					uploadedBy: project.created_by,
					description: documentDescription,
				},
				(progress) => setUploadProgress(progress)
			)

			// Reset and close modal
			setSelectedFile(null)
			setDocumentType('other')
			setDocumentDescription('')
			setShowUploadModal(false)

			// Refresh documents
			onRefreshDocuments?.()
		} catch (error) {
			console.error('Upload failed:', error)
			setUploadError(error.message || 'Failed to upload document')
		} finally {
			setUploading(false)
			setUploadProgress(0)
		}
	}

	// Handle document delete
	const handleDeleteDocument = async (docId) => {
		if (!window.confirm('Are you sure you want to delete this document?')) return

		setDeletingDocId(docId)
		try {
			await projectDocumentService.delete(docId)
			onRefreshDocuments?.()
		} catch (error) {
			console.error('Delete failed:', error)
		} finally {
			setDeletingDocId(null)
		}
	}

	// Handle document download
	const handleDownloadDocument = (doc) => {
		const downloadUrl = projectDocumentService.getDownloadUrl(doc.id)
		window.open(downloadUrl, '_blank')
	}

	// Close upload modal
	const handleCloseUploadModal = () => {
		setShowUploadModal(false)
		setSelectedFile(null)
		setDocumentType('other')
		setDocumentDescription('')
		setUploadError(null)
	}

	// Get file icon based on type
	const getFileIcon = (filename) => {
		const ext = filename?.split('.').pop()?.toLowerCase()
		if (ext === 'pdf') return FileText
		if (['doc', 'docx'].includes(ext)) return File
		return Paperclip
	}

	return (
		<div className="details-tab">
			{/* Basic Information Block */}
			<div className="details-block">
				<div className="details-block__header">
					<div className="details-block__title">
						<Info size={20} />
						<h3>Basic Information</h3>
					</div>
					{canEdit && (
						<Button
							variant="ghost"
							size="small"
							onClick={() => handleEditBlock('basic')}
						>
							<Edit2 size={14} />
							Edit
						</Button>
					)}
				</div>
				<div className="details-block__content">
					<div className="details-field">
						<label className="details-field__label">Project Name</label>
						<p className="details-field__value">{project.name || '-'}</p>
					</div>
					<div className="details-field details-field--full">
						<label className="details-field__label">Description</label>
						<p className="details-field__value details-field__value--text">
							{project.description || 'No description provided'}
						</p>
					</div>
				</div>
			</div>

			{/* Timeline Block */}
			<div className="details-block">
				<div className="details-block__header">
					<div className="details-block__title">
						<Calendar size={20} />
						<h3>Timeline</h3>
					</div>
					{canEdit && (
						<Button
							variant="ghost"
							size="small"
							onClick={() => handleEditBlock('dates')}
						>
							<Edit2 size={14} />
							Edit
						</Button>
					)}
				</div>
				<div className="details-block__content details-block__content--grid">
					<div className="details-field">
						<div className="details-field__icon">
							<Calendar size={16} />
						</div>
						<div className="details-field__info">
							<label className="details-field__label">Start Date</label>
							<p className="details-field__value">{formatDate(project.start_date)}</p>
						</div>
					</div>
					<div className="details-field">
						<div className="details-field__icon">
							<Target size={16} />
						</div>
						<div className="details-field__info">
							<label className="details-field__label">Target Date</label>
							<p className={`details-field__value ${daysInfo?.isOverdue ? 'details-field__value--overdue' : ''}`}>
								{formatDate(project.target_date)}
							</p>
						</div>
					</div>
					<div className="details-field">
						<div className="details-field__icon">
							<Clock size={16} />
						</div>
						<div className="details-field__info">
							<label className="details-field__label">Time Remaining</label>
							<p className={`details-field__value ${daysInfo?.isOverdue ? 'details-field__value--overdue' : ''}`}>
								{daysInfo ? `${daysInfo.value > 0 ? daysInfo.value + ' ' : ''}${daysInfo.label}` : '-'}
							</p>
						</div>
					</div>
				</div>
			</div>

			{/* Business Context Block */}
			<div className="details-block">
				<div className="details-block__header">
					<div className="details-block__title">
						<Building2 size={20} />
						<h3>Business Context</h3>
					</div>
					{canEdit && (
						<Button
							variant="ghost"
							size="small"
							onClick={() => handleEditBlock('business')}
						>
							<Edit2 size={14} />
							Edit
						</Button>
					)}
				</div>
				<div className="details-block__content details-block__content--grid">
					<div className="details-field">
						<div className="details-field__icon">
							<Building2 size={16} />
						</div>
						<div className="details-field__info">
							<label className="details-field__label">Industry</label>
							<p className="details-field__value">{project.industry || '-'}</p>
						</div>
					</div>
					<div className="details-field">
						<div className="details-field__icon">
							<Globe size={16} />
						</div>
						<div className="details-field__info">
							<label className="details-field__label">Domain</label>
							<p className="details-field__value">{project.domain || '-'}</p>
						</div>
					</div>
					<div className="details-field">
						<div className="details-field__icon">
							<Briefcase size={16} />
						</div>
						<div className="details-field__info">
							<label className="details-field__label">Project Type</label>
							<p className="details-field__value">
								{project.type ? project.type.charAt(0).toUpperCase() + project.type.slice(1).replace('_', ' ') : '-'}
							</p>
						</div>
					</div>
				</div>
			</div>

			{/* Compliance Standards Block */}
			<div className="details-block">
				<div className="details-block__header">
					<div className="details-block__title">
						<Shield size={20} />
						<h3>Compliance Standards</h3>
					</div>
				</div>
				<div className="details-block__content">
					{certificates.length > 0 ? (
						<div className="certificates-list">
							{certificates.map((cert, index) => (
								<div key={cert.id || index} className="certificate-item">
									<div className="certificate-item__icon">
										<Shield size={18} />
									</div>
									<div className="certificate-item__info">
										<span className="certificate-item__name">{cert.name || cert.title}</span>
										{cert.description && (
											<span className="certificate-item__desc">{cert.description}</span>
										)}
									</div>
									<span className="certificate-item__badge">Active</span>
								</div>
							))}
						</div>
					) : (
						<div className="details-empty">
							<Shield size={32} />
							<p>No compliance standards assigned</p>
						</div>
					)}
				</div>
			</div>

			{/* Documents Block */}
			<div className="details-block">
				<div className="details-block__header">
					<div className="details-block__title">
						<Paperclip size={20} />
						<h3>Project Documents</h3>
						{documents.length > 0 && (
							<span className="details-block__count">{documents.length}</span>
						)}
					</div>
					{canEdit && (
						<Button
							variant="ghost"
							size="small"
							onClick={() => setShowUploadModal(true)}
						>
							<Upload size={14} />
							Upload
						</Button>
					)}
				</div>
				<div className="details-block__content">
					{documents.length > 0 ? (
						<div className="documents-list">
							{documents.map((doc) => {
								const documentFileName = doc.original_name || doc.file_name || doc.filename || 'Unknown file'
								const FileIcon = getFileIcon(documentFileName)
								return (
									<div key={doc.id} className="document-item">
										<div className="document-item__icon">
											<FileIcon size={20} />
										</div>
										<div className="document-item__info">
											<span className="document-item__name">{documentFileName}</span>
											<div className="document-item__meta">
												<span className="document-item__type">
													{projectDocumentService.getTypeLabel(doc.document_type || doc.type)}
												</span>
												<span className="document-item__divider">•</span>
												<span className="document-item__size">
													{projectDocumentService.formatFileSize(doc.file_size || doc.size || 0)}
												</span>
												<span className="document-item__divider">•</span>
												<span className="document-item__date">
													{formatDate(doc.created_at || doc.uploaded_at)}
												</span>
											</div>
											{doc.description && (
												<span className="document-item__desc">{doc.description}</span>
											)}
										</div>
										<div className="document-item__actions">
											<button
												className="document-item__action"
												onClick={() => handleDownloadDocument(doc)}
												title="Download"
											>
												<Download size={16} />
											</button>
											{/* Delete button hidden for now - may be shown later
											{canEdit && (
												<button
													className="document-item__action document-item__action--danger"
													onClick={() => handleDeleteDocument(doc.id)}
													disabled={deletingDocId === doc.id}
													title="Delete"
												>
													<Trash2 size={16} />
												</button>
											)}
											*/}
										</div>
									</div>
								)
							})}
						</div>
					) : (
						<div className="details-empty">
							<Paperclip size={32} />
							<p>No documents uploaded</p>
							{canEdit && (
								<Button
									variant="outline"
									size="small"
									onClick={() => setShowUploadModal(true)}
								>
									<Upload size={14} />
									Upload Document
								</Button>
							)}
						</div>
					)}
				</div>
			</div>

			{/* Project Metadata Block */}
			<div className="details-block details-block--subtle">
				<div className="details-block__header">
					<div className="details-block__title">
						<FileText size={20} />
						<h3>Project Metadata</h3>
					</div>
				</div>
				<div className="details-block__content details-block__content--grid">
					<div className="details-field details-field--small">
						<label className="details-field__label">Project ID</label>
						<p className="details-field__value details-field__value--mono">#{project.id}</p>
					</div>
					<div className="details-field details-field--small">
						<label className="details-field__label">Created</label>
						<p className="details-field__value">{formatDate(project.created_at)}</p>
					</div>
					<div className="details-field details-field--small">
						<label className="details-field__label">Last Updated</label>
						<p className="details-field__value">{formatDate(project.updated_at)}</p>
					</div>
					<div className="details-field details-field--small">
						<label className="details-field__label">Status</label>
						<p className="details-field__value">
							{project.status ? project.status.charAt(0).toUpperCase() + project.status.slice(1).replace('_', ' ') : '-'}
						</p>
					</div>
				</div>
			</div>

			{/* Edit Basic Info Modal */}
			<Modal
				isOpen={editingBlock === 'basic'}
				onClose={handleCloseModal}
				title="Edit Basic Information"
				size="medium"
				closeOnBackdrop={false}
			>
				<div className="edit-modal-content">
					<div className="form-group">
						<Input
							label="Project Name"
							value={editFormData.name || ''}
							onChange={(e) => handleFieldChange('name', e.target.value)}
							placeholder="Enter project name"
							required
						/>
					</div>
					<div className="form-group">
						<Textarea
							label="Description"
							value={editFormData.description || ''}
							onChange={(e) => handleFieldChange('description', e.target.value)}
							placeholder="Enter project description"
							rows={4}
						/>
					</div>
					<div className="edit-modal-actions">
						<Button variant="outline" onClick={handleCloseModal}>
							Cancel
						</Button>
						<Button variant="primary" onClick={handleSaveBlock} disabled={saving}>
							<Save size={16} />
							{saving ? 'Saving...' : 'Save Changes'}
						</Button>
					</div>
				</div>
			</Modal>

			{/* Edit Dates Modal */}
			<Modal
				isOpen={editingBlock === 'dates'}
				onClose={handleCloseModal}
				title="Edit Timeline"
				size="medium"
				closeOnBackdrop={false}
			>
				<div className="edit-modal-content">
					<div className="form-row">
						<div className="form-group">
							<Input
								type="date"
								label="Start Date"
								value={editFormData.start_date || ''}
								onChange={(e) => handleFieldChange('start_date', e.target.value)}
							/>
						</div>
						<div className="form-group">
							<Input
								type="date"
								label="Target Date"
								value={editFormData.target_date || ''}
								onChange={(e) => handleFieldChange('target_date', e.target.value)}
							/>
						</div>
					</div>
					<div className="edit-modal-actions">
						<Button variant="outline" onClick={handleCloseModal}>
							Cancel
						</Button>
						<Button variant="primary" onClick={handleSaveBlock} disabled={saving}>
							<Save size={16} />
							{saving ? 'Saving...' : 'Save Changes'}
						</Button>
					</div>
				</div>
			</Modal>

			{/* Edit Business Context Modal */}
			<Modal
				isOpen={editingBlock === 'business'}
				onClose={handleCloseModal}
				title="Edit Business Context"
				size="medium"
				closeOnBackdrop={false}
			>
				<div className="edit-modal-content">
					<div className="form-group">
						<Input
							label="Industry"
							value={editFormData.industry || ''}
							onChange={(e) => handleFieldChange('industry', e.target.value)}
							placeholder="e.g., Technology, Healthcare, Finance"
						/>
					</div>
					<div className="form-group">
						<Input
							label="Domain"
							value={editFormData.domain || ''}
							onChange={(e) => handleFieldChange('domain', e.target.value)}
							placeholder="e.g., SaaS, E-commerce, Cloud Services"
						/>
					</div>
					<div className="form-group">
						<Select
							label="Project Type"
							value={editFormData.type || ''}
							onChange={(e) => handleFieldChange('type', e.target.value)}
							options={[
								{ value: '', label: 'Select project type' },
								...projectTypeOptions,
							]}
						/>
					</div>
					<div className="edit-modal-actions">
						<Button variant="outline" onClick={handleCloseModal}>
							Cancel
						</Button>
						<Button variant="primary" onClick={handleSaveBlock} disabled={saving}>
							<Save size={16} />
							{saving ? 'Saving...' : 'Save Changes'}
						</Button>
					</div>
				</div>
			</Modal>

			{/* Upload Document Modal */}
			<Modal
				isOpen={showUploadModal}
				onClose={handleCloseUploadModal}
				title="Upload Document"
				size="medium"
				closeOnBackdrop={false}
			>
				<div className="edit-modal-content">
					{uploadError && (
						<div className="upload-error">
							<AlertCircle size={16} />
							<span>{uploadError}</span>
						</div>
					)}

					<div className="form-group">
						<label className="form-label">Select File</label>
						<div
							className={`file-dropzone ${selectedFile ? 'file-dropzone--has-file' : ''}`}
							onClick={() => fileInputRef.current?.click()}
						>
							{selectedFile ? (
								<div className="file-dropzone__selected">
									<File size={24} />
									<div className="file-dropzone__info">
										<span className="file-dropzone__name">{selectedFile.name}</span>
										<span className="file-dropzone__size">
											{projectDocumentService.formatFileSize(selectedFile.size)}
										</span>
									</div>
									<button
										type="button"
										className="file-dropzone__remove"
										onClick={(e) => {
											e.stopPropagation()
											setSelectedFile(null)
										}}
									>
										<Trash2 size={16} />
									</button>
								</div>
							) : (
								<div className="file-dropzone__placeholder">
									<Upload size={32} />
									<span>Click to select a file</span>
									<span className="file-dropzone__hint">
										PDF, DOC, DOCX, XLS, XLSX, PNG, JPG up to 10MB
									</span>
								</div>
							)}
						</div>
						<input
							ref={fileInputRef}
							type="file"
							onChange={handleFileSelect}
							accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
							style={{ display: 'none' }}
						/>
					</div>

					<div className="form-group">
						<Select
							label="Document Type"
							value={documentType}
							onChange={(e) => setDocumentType(e.target.value)}
							options={documentTypeOptions}
						/>
					</div>

					<div className="form-group">
						<Textarea
							label="Description (Optional)"
							value={documentDescription}
							onChange={(e) => setDocumentDescription(e.target.value)}
							placeholder="Brief description of the document"
							rows={3}
						/>
					</div>

					{uploading && (
						<div className="upload-progress">
							<div className="upload-progress__bar">
								<div
									className="upload-progress__fill"
									style={{ width: `${uploadProgress}%` }}
								/>
							</div>
							<span className="upload-progress__text">{uploadProgress}% Uploading...</span>
						</div>
					)}

					<div className="edit-modal-actions">
						<Button variant="outline" onClick={handleCloseUploadModal} disabled={uploading}>
							Cancel
						</Button>
						<Button
							variant="primary"
							onClick={handleUploadDocument}
							disabled={!selectedFile || uploading}
						>
							<Upload size={16} />
							{uploading ? 'Uploading...' : 'Upload Document'}
						</Button>
					</div>
				</div>
			</Modal>
		</div>
	)
}

DetailsTab.propTypes = {
	project: PropTypes.object.isRequired,
	certificates: PropTypes.array,
	documents: PropTypes.array,
	canEdit: PropTypes.bool,
	onUpdateProject: PropTypes.func,
	onRefreshDocuments: PropTypes.func,
}

DetailsTab.defaultProps = {
	certificates: [],
	documents: [],
	canEdit: false,
}

export default DetailsTab
